/**
 * Settings Management Page
 * Manage website settings (email, phone, address, social links)
 */

import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ImageUpload from "components/ImageUpload";

import {
  extractYouTubeId,
  fieldError,
  validationErrors,
  toggleChecked,
  buildSaveItems,
  PILLAR_TOGGLES,
} from "./settingsLogic";
import { adminApi } from "../../services/api";

// Labels are inline spans inside a 20px/32.5px parent, so a label that wraps on a phone gets
// 32px between its lines. Below md make them blocks with their own line height.
const fieldLabelSx = { display: { xs: "block", md: "inline" } };

function Settings() {
  // Names that exist in the database, and names the user has edited this session.
  const [savedNames, setSavedNames] = useState(() => new Set());
  const [touchedNames, setTouchedNames] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({});

  // The site's top navigation is four mega-menu pillars (see PILLAR_TOGGLES).
  const menuToggles = PILLAR_TOGGLES;

  // The 4 courses that have their own live webinar sessions — each needs its
  // own Zoom/date settings instead of sharing the one global set below.
  // `data-ai` was excluded here originally (see spec
  // 2026-09-07-webinar-distinct-course-pages-design.md §1.4 / §3.3) because
  // it was still an unpublished placeholder with no host at the time; it's
  // now published with a real host and real autoresponder content, so it
  // needs its own settings the same as the other 3 (added 2026-09-11).
  // `general` intentionally still has no entry — it's the retained legacy
  // funnel and continues to use the shared default settings below, by design.
  const WEBINAR_COURSES = [
    { slug: "cloud-network-infrastructure", label: "Cloud & Network Infrastructure" },
    { slug: "software-engineering", label: "Software Engineering" },
    { slug: "cyber-security", label: "Cyber Security" },
    { slug: "data-ai", label: "Data & AI" },
  ];

  const webinarCourseFields = [];
  WEBINAR_COURSES.forEach((course) => {
    webinarCourseFields.push(
      {
        name: `webinar_${course.slug}_zoom_url`,
        label: `${course.label} — Zoom Join Link`,
        description: `Full Zoom meeting URL for ${course.label} registrants — overrides the default Zoom Join Link above for this course only`,
      },
      {
        name: `webinar_${course.slug}_zoom_id`,
        label: `${course.label} — Meeting ID`,
        description: `Zoom Meeting ID shown in confirmation messages for ${course.label}`,
      },
      {
        name: `webinar_${course.slug}_zoom_passcode`,
        label: `${course.label} — Passcode`,
        description: `Zoom meeting passcode for ${course.label}`,
      },
      {
        name: `webinar_${course.slug}_zoom_chat_url`,
        label: `${course.label} — Zoom Chat Link`,
        description: `Zoom in-meeting chat URL for ${course.label}`,
      },
      {
        name: `webinar_${course.slug}_date`,
        label: `${course.label} — Training Date`,
        description: `Human-readable training date for ${course.label} — e.g. 'Saturday, 27th June 2026'`,
      },
      {
        name: `webinar_${course.slug}_time`,
        label: `${course.label} — Training Time`,
        description: `Session start time for ${course.label} — e.g. '7:00 PM'`,
      },
      {
        name: `webinar_${course.slug}_whatsapp_group`,
        label: `${course.label} — WhatsApp Group Link`,
        description: `Invite link for the ${course.label} participants WhatsApp group`,
      },
      {
        name: `webinar_${course.slug}_start_iso`,
        label: `${course.label} — Session Start (ISO 8601)`,
        description: `Exact start instant for ${course.label}, with timezone offset — e.g. '2026-09-26T20:00:00+01:00'. Drives the landing page countdown and the "24 hours before / 2 hours after" follow-up emails.`,
        type: "iso-datetime",
      },
      {
        name: `webinar_${course.slug}_booking_link`,
        label: `${course.label} — Clarity Call Booking Link`,
        description: `Where "Book it here" in ${course.label} follow-up emails points ({{bookingLink}}). Falls back to the autoresponder Booking Link.`,
      },
      {
        name: `webinar_${course.slug}_email_from_name`,
        label: `${course.label} — Email Sender Name`,
        description: `Name ${course.label} emails to registrants and bookers are sent from — e.g. 'Madue Godwin'. Leave blank to use the default sender (Elite Support), or the Default Email Sender Name below.`,
      },
      {
        name: `webinar_${course.slug}_email_from_address`,
        label: `${course.label} — Email Sender Address`,
        description: `Address ${course.label} emails are sent from. The mailbox must already exist in Microsoft 365 and be licensed (a shared mailbox needs no license), and the email app must be allowed to send as it. If it can't, emails fall back to the default sender and a warning is logged. Leave blank for the default sender.`,
        type: "email-address",
      }
    );
  });

  // Setting groups
  const settingGroups = {
    contact: [
      { name: "site_email", label: "Site Email", description: "Primary contact email" },
      {
        name: "site_phone",
        label: "Phone Number",
        description:
          "Primary contact phone (fallback when a region-specific number below isn't set)",
      },
      { name: "site_phone_2", label: "Secondary Phone", description: "Alternative contact phone" },
      {
        name: "site_phone_uk",
        label: "Phone Number (UK)",
        description: "Contact phone shown to UK/international visitors",
      },
      {
        name: "site_phone_ng",
        label: "Phone Number (Nigeria)",
        description: "Contact phone shown to Nigeria/West Africa visitors",
      },
      { name: "site_address", label: "Address", description: "Physical office address" },
      { name: "site_city", label: "City", description: "City location" },
      { name: "site_country", label: "Country", description: "Country location" },
    ],
    social: [
      { name: "social_youtube", label: "YouTube URL", description: "YouTube channel link" },
      { name: "social_tiktok", label: "TikTok URL", description: "TikTok profile link" },
      { name: "social_instagram", label: "Instagram URL", description: "Instagram profile link" },
      { name: "social_twitter", label: "X (Twitter) URL", description: "X (Twitter) profile link" },
      { name: "social_whatsapp", label: "WhatsApp URL", description: "WhatsApp channel link" },
      { name: "social_linkedin", label: "LinkedIn URL", description: "LinkedIn company page" },
    ],
    general: [
      { name: "site_name", label: "Site Name", description: "Website name/title" },
      { name: "site_tagline", label: "Site Tagline", description: "Short description/tagline" },
      { name: "site_description", label: "Site Description", description: "Full site description" },
      { name: "site_logo", label: "Logo", description: "Main logo image", type: "image" },
      {
        name: "site_logo_white",
        label: "Logo (White)",
        description: "White version of logo for dark backgrounds",
        type: "image",
      },
      {
        name: "site_favicon",
        label: "Favicon",
        description: "Browser favicon image",
        type: "image",
      },
      {
        name: "service_brochure",
        label: "Service Brochure",
        description: "Upload a PDF or document for your service brochure",
        type: "file",
      },
      // Homepage section toggles
      {
        name: "show_home_hero",
        label: "Show Hero Section",
        description: "Display the hero/banner section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_about",
        label: "Show About Section",
        description: "Display the about section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_service",
        label: "Show Service Section",
        description: "Display the services section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_team",
        label: "Show Team Section",
        description: "Display the team members section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_cta",
        label: "Show Call To Action Section",
        description: "Display the call-to-action section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_blog",
        label: "Show Blog Section",
        description: "Display the blog/news section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_testimonial",
        label: "Show Testimonial Section",
        description: "Display the testimonial section on the homepage",
        type: "toggle",
      },
      {
        name: "show_home_partners",
        label: "Show Partners Section",
        description: "Display the partners section on the homepage",
        type: "toggle",
      },
      {
        name: "partners_json",
        label: "Partners",
        description:
          'JSON list of partner logos shown on the homepage, e.g. [{"name":"Acme","logo":"https://.../acme.png"}]. Leave empty to hide the section.',
      },
      // Navbar/Footer menu toggles
      {
        name: "show_navbar_menu",
        label: "Show Navbar Menu",
        description: "Display the main navigation bar menu",
        type: "toggle",
      },
      {
        name: "show_footer_menu",
        label: "Show Footer Menu",
        description: "Display the footer menu at the bottom of the site",
        type: "toggle",
      },
    ],
    menus: menuToggles,
    other: [
      { name: "working_hours", label: "Working Hours", description: "Business hours" },
      { name: "support_email", label: "Support Email", description: "Technical support email" },
      { name: "sales_email", label: "Sales Email", description: "Sales inquiries email" },
    ],
    autoresponder: [
      {
        name: "autoresponder_booking_link",
        label: "Booking Link",
        description:
          "Full URL to the booking/application page — used as {{bookingLink}} in emails and WhatsApp messages",
      },
      {
        name: "autoresponder_whatsapp_link",
        label: "WhatsApp Link",
        description: "Direct WhatsApp link for the sales rep — used as {{whatsappLink}}",
      },
      {
        name: "autoresponder_cohort_date",
        label: "Next Cohort Date",
        description:
          "Human-readable cohort start date — e.g. 'the second week of July 2026' — used as {{cohortDate}}",
      },
      {
        name: "autoresponder_spots_remaining",
        label: "Spots Remaining",
        description:
          "Current number of open spots in the cohort — used as {{spotsRemaining}}. Update this regularly.",
      },
      {
        name: "autoresponder_rep_name",
        label: "Sales Rep Name",
        description:
          "Full name of the sales rep who signs off emails and WhatsApp messages — used as {{repName}}",
      },
      {
        name: "autoresponder_currency_uk",
        label: "Price (UK)",
        description:
          "Programme price shown to UK leads — e.g. £450/month — used as {{currency}} when contact region is UK",
      },
      {
        name: "autoresponder_currency_ng",
        label: "Price (Nigeria)",
        description:
          "Programme price shown to Nigeria leads — e.g. ₦512,500 — used as {{currency}} when contact region is Nigeria",
      },
    ],
    webinar: [
      {
        name: "webinar_zoom_url",
        label: "Default Zoom Join Link",
        description:
          "Fallback Zoom meeting URL used by any course with no override below — used as {{zoomUrl}}",
      },
      {
        name: "webinar_zoom_id",
        label: "Default Meeting ID",
        description: "Fallback Zoom Meeting ID — used as {{zoomId}}",
      },
      {
        name: "webinar_zoom_passcode",
        label: "Default Passcode",
        description: "Fallback Zoom meeting passcode — used as {{zoomPasscode}}",
      },
      {
        name: "webinar_zoom_chat_url",
        label: "Default Zoom Chat Link",
        description: "Fallback Zoom in-meeting chat URL — used as {{zoomChatUrl}}",
      },
      {
        name: "webinar_date",
        label: "Default Training Date",
        description:
          "Fallback human-readable training date — e.g. 'Saturday, 27th June 2026' — used as {{webinarDate}}",
      },
      {
        name: "webinar_time",
        label: "Default Training Time",
        description: "Fallback session start time — e.g. '7:00 PM' — used as {{webinarTime}}",
      },
      {
        name: "webinar_whatsapp_group",
        label: "Default WhatsApp Group Link",
        description: "Fallback invite link for the webinar participants WhatsApp group",
      },
      {
        name: "webinar_replay_url",
        label: "Replay Video URL",
        description:
          "YouTube URL of the previous session recording — shown on the /replay page. Accepts youtube.com/watch?v=… or youtu.be/… links.",
        type: "youtube-url",
      },
      {
        name: "webinar_start_iso",
        label: "Default Session Start (ISO 8601)",
        description: "Fallback exact start instant with offset — e.g. '2026-09-26T20:00:00+01:00'",
        type: "iso-datetime",
      },
      {
        name: "webinar_email_from_name",
        label: "Default Email Sender Name",
        description:
          "Fallback sender name for every course's registrant/booker emails that has no Email Sender Name of its own below. Leave blank to keep the default sender (Elite Support).",
      },
      {
        name: "webinar_email_from_address",
        label: "Default Email Sender Address",
        description:
          "Fallback sender address for any course with no Email Sender Address of its own below. The mailbox must already exist in Microsoft 365 and be licensed (a shared mailbox needs no license), and the email app must be allowed to send as it. Leave blank to keep the default sender.",
        type: "email-address",
      },
      ...webinarCourseFields,
    ],
  };

  const tabLabels = [
    "Contact Info",
    "Social Media",
    "General Settings",
    "Menus",
    "Other",
    "Autoresponder",
    "Webinar",
  ];
  const tabCategories = [
    "contact",
    "social",
    "general",
    "menus",
    "other",
    "autoresponder",
    "webinar",
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      // Initialize form data from settings
      const data = {};
      response.forEach((setting) => {
        data[setting.name] = setting.value;
      });
      setFormData(data);
      setSavedNames(new Set(response.map((setting) => setting.name)));
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch settings");
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouchedNames((prev) => new Set(prev).add(name));
  };

  const handleSave = async () => {
    try {
      setError("");
      setSuccess("");

      // Get current tab category
      const currentCategory = tabCategories[activeTab];
      const currentSettings = settingGroups[currentCategory];

      const problems = validationErrors(currentSettings, formData);
      if (problems.length > 0) {
        setError(`Fix these before saving: ${problems.join(" · ")}`);
        return;
      }

      const settingsToUpdate = buildSaveItems(
        currentSettings,
        formData,
        savedNames,
        touchedNames,
        currentCategory
      );
      await adminApi.bulkUpdateSettings(settingsToUpdate);
      // Do not reload: that would overwrite unsaved edits on the other tabs.
      setSavedNames((prev) => {
        const next = new Set(prev);
        settingsToUpdate.forEach((item) => next.add(item.name));
        return next;
      });
      setSuccess("Settings saved successfully!");

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Failed to save settings");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
    setSuccess("");
  };

  const renderSettingField = (setting) => {
    const value = formData[setting.name] || "";
    if (setting.type === "youtube-url") {
      const videoId = value ? extractYouTubeId(value) : null;
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
              {setting.label}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {setting.description}
            </MDTypography>
            <MDInput
              type="text"
              fullWidth
              value={value}
              onChange={(e) => handleChange(setting.name, e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
            />
            {value && !videoId && (
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                Not a recognised YouTube URL — enter a youtube.com/watch?v=… or youtu.be/… link.
              </MDTypography>
            )}
            {videoId && (
              <MDBox
                mt={2}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  aspectRatio: "16/9",
                  maxWidth: 560,
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                  title="Replay preview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                />
              </MDBox>
            )}
          </MDBox>
        </Grid>
      );
    }
    if (setting.type === "iso-datetime") {
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
              {setting.label}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {setting.description}
            </MDTypography>
            <MDInput
              type="text"
              fullWidth
              value={value}
              onChange={(e) => handleChange(setting.name, e.target.value)}
              placeholder="2026-09-26T20:00:00+01:00"
            />
            {value && fieldError(setting, value) && (
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                Use ISO 8601 with a timezone offset, e.g. 2026-09-26T20:00:00+01:00
              </MDTypography>
            )}
          </MDBox>
        </Grid>
      );
    }
    if (setting.type === "email-address") {
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
              {setting.label}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {setting.description}
            </MDTypography>
            <MDInput
              type="text"
              fullWidth
              value={value}
              onChange={(e) => handleChange(setting.name, e.target.value)}
              placeholder="name@elite247hub.com"
            />
            {value && fieldError(setting, value) && (
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                Not a valid email address — it will be ignored and the default sender used instead.
              </MDTypography>
            )}
          </MDBox>
        </Grid>
      );
    }
    if (setting.type === "toggle") {
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <div>
              <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
                {setting.label}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                {setting.description}
              </MDTypography>
            </div>
            <input
              type="checkbox"
              checked={toggleChecked(setting, formData)}
              onChange={(e) => handleChange(setting.name, e.target.checked)}
              style={{ width: 32, height: 32, flexShrink: 0 }}
            />
          </MDBox>
        </Grid>
      );
    }
    if (setting.type === "image") {
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
              {setting.label}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {setting.description}
            </MDTypography>
            <ImageUpload
              currentImage={value}
              onImageChange={(url) => handleChange(setting.name, url)}
              label={setting.label}
            />
          </MDBox>
        </Grid>
      );
    }
    if (setting.type === "file") {
      return (
        <Grid item xs={12} key={setting.name}>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
              {setting.label}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              {setting.description}
            </MDTypography>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append("file", file);
                try {
                  const res = await adminApi.uploadFile(formData);
                  // Always save only the file URL as a string (never an object)
                  let fileUrl = "";
                  if (typeof res === "string") {
                    fileUrl = res;
                  } else if (res && typeof res === "object") {
                    // If the response is an object, extract the first string value
                    fileUrl = res.url || res.path || res.file || "";
                    // If still an object, try to stringify deeply nested value
                    if (typeof fileUrl === "object" && fileUrl !== null) {
                      fileUrl = fileUrl.url || fileUrl.path || fileUrl.file || "";
                    }
                  }
                  if (typeof fileUrl !== "string") fileUrl = "";
                  handleChange(setting.name, fileUrl);
                } catch (err) {
                  alert("File upload failed");
                }
              }}
            />
            {value && (
              <MDTypography variant="caption" color="info" display="block" mt={1}>
                Current file:{" "}
                <a href={value} target="_blank" rel="noopener noreferrer">
                  Download
                </a>
              </MDTypography>
            )}
          </MDBox>
        </Grid>
      );
    }
    return (
      <Grid item xs={12} key={setting.name}>
        <MDBox mb={2}>
          <MDTypography variant="button" fontWeight="regular" color="text" sx={fieldLabelSx}>
            {setting.label}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            {setting.description}
          </MDTypography>
          <MDInput
            type="text"
            fullWidth
            value={value}
            onChange={(e) => handleChange(setting.name, e.target.value)}
            placeholder={`Enter ${setting.label.toLowerCase()}`}
          />
        </MDBox>
      </Grid>
    );
  };

  const renderTabContent = () => {
    const currentCategory = tabCategories[activeTab];
    const currentSettings = settingGroups[currentCategory];

    return (
      <Grid container spacing={2}>
        {currentSettings.map((setting) => renderSettingField(setting))}
        <Grid item xs={12}>
          <MDBox mt={3} display="flex" justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSave}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Icon>save</Icon>&nbsp; Save Changes
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox p={{ xs: 2, sm: 3 }} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Website Settings
              </MDTypography>
              <MDTypography variant="button" color="text">
                Manage website content like email, phone, address, and social links
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>

        {error && (
          <MDBox mb={3}>
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          </MDBox>
        )}

        {success && (
          <MDBox mb={3}>
            <Alert severity="success" onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </MDBox>
        )}

        <Card>
          <MDBox p={{ xs: 2, sm: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>
            <Divider />
            <MDBox mt={3}>
              {loading ? (
                <MDTypography variant="body2" color="text">
                  Loading settings...
                </MDTypography>
              ) : (
                renderTabContent()
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default Settings;
